import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { PatientService } from 'src/app/core/api/patient.service';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';
import { upsertImpressionsSection } from '../impressions/impressions-sections.util';
import {
  RiskFactor,
  RiskFactorDisplay,
  modifiableRiskFactors,
  nonModifiableRiskFactors,
  riskLevelToNameMap
} from './risk-factors.model';
import { RiskFactorsService } from './risk-factors.service';

interface RiskFactorData {
  medicalHistoryData: any;
  impressionsControl: FormControl;
  closeAfterProcess?: boolean;
}

@Component({
  selector: 'csi-risk-factors-modal',
  templateUrl: './risk-factors-modal.component.html',
  styleUrls: ['./risk-factors-modal.component.css']
})
export class RiskFactorsModalComponent implements OnInit {
  @ViewChild('riskFactorsEl', { static: false }) riskFactorsEl: ElementRef;
  public nonModifiableData = null;
  public isCopied = false;
  public modifiableRiskFactorsDisplay: RiskFactorDisplay[] = [];
  public nonModifiableRiskFactorsDisplay: RiskFactorDisplay[] = [];
  public riskLevelToNameMap = riskLevelToNameMap;
  public modalView = true;
  public riskFactorTitle: string;
  public lang: string = this.translocoService.getActiveLang();
  public translateObject = this.translocoService.getTranslation(
    this.translocoService.getActiveLang()
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RiskFactorData,
    public patientService: PatientService,
    public translocoService: TranslocoService,
    public riskFactorsService: RiskFactorsService,
    private confirmModalService: ConfirmModalService,
    private changeRef: ChangeDetectorRef,
    private dialogRef: MatDialogRef<RiskFactorsModalComponent>
  ) {}

  ngOnInit() {
    if (this.patientService.patient) {
      this.nonModifiableData = JSON.parse(this.patientService.patient.nonModifiableData);
    }
    this.evaluateRiskLevels(
      modifiableRiskFactors,
      this.modifiableRiskFactorsDisplay,
      this.data.medicalHistoryData
    );
    this.evaluateRiskLevels(
      nonModifiableRiskFactors,
      this.nonModifiableRiskFactorsDisplay,
      this.nonModifiableData
    );
  }

  evaluateRiskLevels(
    riskFactors: { [key: string]: RiskFactor },
    riskFactorDisplay: RiskFactorDisplay[],
    data: any
  ) {
    Object.keys(riskFactors).forEach(key => {
      const medicalHistoryValue = data && data[key];
      if (medicalHistoryValue !== undefined && medicalHistoryValue !== null) {
        const medicalHistoryValue = data[key];
        let riskLevel = null;
        let contributor = null;
        let condition = null;
        riskFactors[key].indicators.some(indicator => {
          if (
            this.riskFactorsService.compareValues(
              medicalHistoryValue,
              indicator.condition,
              indicator.operator
            )
          ) {
            riskLevel = indicator.riskLevel;
            condition = indicator.condition;
            contributor = indicator.contributor ? indicator.contributor : null;
            return true;
          }
        });

        if (riskLevel) {
          riskFactorDisplay.push({
            key: key,
            condition: condition,
            name: riskFactors[key].name,
            riskLevel: riskLevel,
            contributor: contributor
          });
        }
      }
    });
    riskFactorDisplay.sort((a, b) => this.riskFactorsService.compareRiskFactorDisplay(a, b));
  }

  public copyToImpressions() {
    this.confirmModalService
      .show(
        'Overwrite Risk Factors section?',
        "This will replace only the Risk Factors section in Impressions. Other impression text and Doctor's Notes will not be changed.",
        'Overwrite Section',
        'Cancel'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.modalView = false;
        this.changeRef.detectChanges();
        const currentImpressions = (this.data.impressionsControl.value || '').toString();
        this.data.impressionsControl.setValue(
          upsertImpressionsSection(
            currentImpressions,
            'riskFactors',
            this.riskFactorsEl.nativeElement.innerHTML
          )
        );
        this.isCopied = true;
        this.modalView = true;
        this.changeRef.detectChanges();
        this.dialogRef.close();
      });
  }

  ngAfterViewInit() {
    if (this.data.closeAfterProcess) {
      setTimeout(() => {
        const processedHtml = this.riskFactorsEl.nativeElement.innerHTML;
        const riskFactorsTitle = this.translocoService.translate('Dry Eye Risk Factors');
        const aiDisclaimer = this.translocoService.translate(
          'INTERPRETATION.riskFactorsAiDisclaimer'
        );
        this.dialogRef.close(
          `<h4><span style="text-decoration: underline;">${riskFactorsTitle}</span> (${aiDisclaimer})</h4>
          ${processedHtml}
          `
        );
      });
    }
  }

  public changeLanguage(lang = 'en') {
    this.translocoService.load(lang).subscribe(() => {
      this.translocoService.setActiveLang(lang);
      this.translateObject = this.translocoService.getTranslation(
        this.translocoService.getActiveLang()
      );
    });
  }
}
